import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventIcon from "@mui/icons-material/Event";
import { getCityDisplay } from "../utils/city";
import MapAddressLink from "./MapAddressLink";
import { useTranslation } from "react-i18next";

export const formatDurationHhMm = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return null;
  const total = Math.max(0, Math.round(Number(minutes)));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

export const formatHumanLayover = (minutes, t) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours > 0 && mins > 0) {
    return t("transferCard.layoverHoursMinutes", { hours, minutes: mins });
  }
  if (hours > 0) {
    return t("transferCard.layoverHours", { hours });
  }
  return t("transferCard.layoverMinutes", { minutes: mins });
};

/**
 * Compact depart | duration/hub | arrive row (TransferItineraryCard pattern).
 * @param {{ summary: object|null, label?: string }} props
 */
const DirectionJourneySummary = ({ summary, label }) => {
  const { t } = useTranslation("common");

  if (!summary?.departDt) return null;

  const durationText = formatDurationHhMm(summary.totalDurationMinutes);
  const showHub = Boolean(summary.isTransfer && summary.hub);
  const hubName = showHub ? getCityDisplay(summary.hub) : "";
  const layoverText = showHub
    ? formatHumanLayover(summary.layoverMinutes || 0, t)
    : "";

  return (
    <Box>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 1.5, color: "#1F2937" }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 2, sm: 2.5 },
        }}
      >
        <Box sx={{ flex: { sm: 1 }, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              component="span"
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {summary.departDt.format("HH:mm")}
            </Typography>
            <Chip
              size="small"
              icon={<EventIcon sx={{ fontSize: 14 }} />}
              label={summary.departDt.format("DD.MM.YYYY")}
              sx={{
                height: 24,
                fontSize: "0.75rem",
                bgcolor: "action.hover",
                "& .MuiChip-icon": { color: "primary.main" },
              }}
            />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
            {getCityDisplay(summary.fromCity)}
          </Typography>
          {summary.fromAddress && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8125rem" }}
            >
              <MapAddressLink
                city={getCityDisplay(summary.fromCity)}
                address={summary.fromAddress}
              />
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: { sm: "0 0 auto" },
            width: { xs: "100%", sm: 160 },
          }}
        >
          {durationText && (
            <Chip
              size="small"
              icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
              label={durationText}
              sx={{
                mb: 1,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "& .MuiChip-icon": { color: "primary.contrastText" },
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: 2,
                borderRadius: 1,
                bgcolor: "divider",
              }}
            />
            <DirectionsBusIcon
              sx={{ color: "primary.main", fontSize: 28 }}
              aria-hidden
            />
            <Box
              sx={{
                flex: 1,
                height: 2,
                borderRadius: 1,
                bgcolor: "divider",
              }}
            />
          </Box>
          {showHub && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75, textAlign: "center", lineHeight: 1.35 }}
            >
              {t("transferCard.viaHub", {
                city: hubName,
                duration: layoverText,
              })}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flex: { sm: 1 },
            minWidth: 0,
            textAlign: { xs: "left", sm: "right" },
          }}
        >
          {summary.arriveDt ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Typography
                  component="span"
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {summary.arriveDt.format("HH:mm")}
                </Typography>
                <Chip
                  size="small"
                  icon={<EventIcon sx={{ fontSize: 14 }} />}
                  label={summary.arriveDt.format("DD.MM.YYYY")}
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    bgcolor: "action.hover",
                    "& .MuiChip-icon": { color: "primary.main" },
                  }}
                />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
                {getCityDisplay(summary.toCity)}
              </Typography>
              {summary.toAddress && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.8125rem" }}
                >
                  <MapAddressLink
                    city={getCityDisplay(summary.toCity)}
                    address={summary.toAddress}
                  />
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {getCityDisplay(summary.toCity)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DirectionJourneySummary;
